// FP 자원 간트 — 하단 상세탭 ① 생산 계획 상세
//
// old: `analysis/resourcegantt/tabs/ResourcePlanDetailTab.jsx`
//
// **API 호출이 없다.** old는 TreeGrid의 RunBox에서 `activityData`(JSON 문자열)를 역파싱해
// (`common/utils/gantt.js:530 getGanttRowData`) 선택 행의 활동 목록을 만들었는데, new는
// `ResourceGantt.js`의 `buildBoxes(row.activities)`가 **full `ActivityDto`를 `box.activity`에 그대로
// 들고 있다** — 그래서 화면이 `buildResourcePlanRows(row)`로 조립해 `rows`로 넘겨준다.
//
// old 컬럼 정의와 1:1이다. `groupShowMode`의 매핑만 옮겨 적는다:
//   old "always" → `columnGroupShow` 생략(접혀도 보임)
//   old "expand" → `columnGroupShow: "open"`(그룹을 펼칠 때만 보임)
//   old `expandable: true, expanded: false` → 그룹 `openByDefault: false`
//
// **공장·자원은 컬럼이 아니라 그리드 위의 "문맥 밴드"다**(2026-08-24 사용자 선택, A-2안.
// 시안 `prototypes/gantt-detail-tab-layout.html`, 경과 §7-23).
//
// 이 그리드는 **선택한 바 하나의 자원에 대한 활동 목록**이라 공장·자원이 **전 행 동일**하다.
// 처음엔 그걸 근거로 행 그룹 트리(`buildGroupColumnPair` + `REPORT_TREE_GRID_OPTIONS`)로 접었는데,
// 실화면에서 세 가지가 드러났다:
//   ① **접을 대상이 없다** — 그룹이 각각 1개뿐이라 셰브론이 폭·시선만 먹는다.
//   ② **스크롤하면 문맥이 사라진다** — 트리든 병합이든 값은 첫 행에만 남는다. old RealGrid는
//      `mergeRule`이 보이는 영역 기준으로 병합 셀을 다시 그려 이걸 피했는데 AG Grid엔 그 동작이
//      없다(`deferred-features.md` planresult 항목의 기존 한계).
//   ③ 왼쪽 약 460px가 **상수 2개**를 표시하는 데 쓰인다.
// → 전 행이 같은 값이면 그건 컬럼이 아니라 **제목**이다. 그리드 밖으로 빼면 셋이 한 번에 풀린다.
//
// **품목은 그대로 그리드에 둔다** — 활동마다 값이 바뀌므로 문맥이 아니라 데이터다.
// 동일값 연속 병합(`colDef.spanRows`)이 old mergeRule criteria "value"와 같은 규칙이다.

import { useMemo, useRef } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { red } from "@mui/material/colors";

import { format } from "date-fns";

import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import DetailContextBand from "@plannel/pages/factory-plan/report/resource-gantt/DetailContextBand";

const DATETIME_FORMAT = "yyyy-MM-dd HH:mm:ss";

// 소요 시간 컬럼(작업 시간·대기·준비·이동·작업 교체 시간) 공통 폭. 값은 짧은 숫자인데 **헤더가
// 길어서**(최장 "작업 전 대기 시간" 8자 + 우측 정렬 메뉴 아이콘) 폭이 결정된다. 한 값으로 묶어둬야
// 그룹을 펼쳤을 때 계열이 어긋나지 않는다 — 조정은 여기 한 곳만 바꾼다.
const TIME_COL_WIDTH = 160;
// 일시(작업 교체 시작/종료) 컬럼 공통 폭 — "2026-08-21 02:00:00"(19자)이 잘리지 않는 값.
const DATETIME_COL_WIDTH = 190;

// old dataType:"datetime" 표시 대응. 값은 ActivityDto의 LocalDateTime 직렬화 문자열이다.
const formatDateTime = (params) => {
  if (!params.value) return "";
  const date = new Date(params.value);
  return Number.isNaN(date.getTime()) ? String(params.value) : format(date, DATETIME_FORMAT);
};

// `common/grid.js`의 columnType `centerAligned`와 같은 값이다. **직접 들고 있어야 하는 이유**:
// AG Grid는 `colDef.cellStyle`이 있으면 type이 준 `cellStyle`을 병합하지 않고 **덮어쓴다**.
// 계획 시작/종료는 지연 강조(`lateCellStyle`)를 위해 자기 `cellStyle`이 필요하므로, 그 함수가
// 중앙 정렬까지 **스스로** 반환해야 한다(`dayOfWeekHeaderClass`가 `headerClass`에서 겪은 것과
// 같은 부류 — grid.js:286).
const CENTER_CELL = { display: "flex", justifyContent: "center", alignItems: "center" };

// old styleCallback — `lateYn`이면 계획 시작/종료 시각 셀을 `error-column`으로 칠했다.
// 배경 틴트만 쓴다(굵게 없음) — 다른 FP Report 화면의 강조 관례와 같다.
const lateCellStyle = (params) => (
  params.data?.lateYn
    ? { ...CENTER_CELL, backgroundColor: red[50], color: red[800] }
    : CENTER_CELL
);

// 동일 품목 연속 병합. `nodeA`/`nodeB`는 nullable(`SpanRowsParams`)이라 널 가드 없이 접근하면
// 조회 시 TypeError가 난다.
const spanSameItem = ({ nodeA, nodeB, valueA, valueB }) => (
  !!nodeA?.data && !!nodeB?.data && valueA === valueB
);

const ResourcePlanDetailTab = ({ rows, viewName, t }) => {
  const gridRef = useRef(null);

  // 컬럼 헤더는 `headerName`에 번역된 문구를 넣어 정의 시점에 직접 번역한다(팝업류와 같은 대안 패턴).
  // 읽기전용 보조 그리드 3개가 한 화면에 있어 `GridUtils.setColumnDefs`의 p13n 상태를 공유하면
  // 탭끼리 컬럼 상태가 섞인다 — 그래서 이쪽을 택했다. `t`를 의존성에 두어 언어 전환에 따라온다.
  const columnDefs = useMemo(() => [
    {
      // 품목은 트리가 아니라 병합이다(파일 머리말 참고). 명칭도 같이 병합한다 — 코드만 병합하면
      // 그룹을 펼쳤을 때 **병합된 코드 옆에 낱개로 반복되는 명칭**이 붙어 깨져 보인다.
      headerName: t("FP_ITEM"), openByDefault: false, children: [
        { headerName: t("FP_ITEM_CD"), field: "itemCd", width: 140, spanRows: spanSameItem },
        { headerName: t("FP_ITEM_NM"), field: "itemNm", width: 140, columnGroupShow: "open", spanRows: spanSameItem }
      ]
    },
    {
      // old는 이 그룹만 expandable:false — 전부 always라 토글이 필요 없다.
      headerName: t("FP_ACTIVITY"), children: [
        { headerName: t("FP_PLAN_START_DATE"), field: "startTs", width: 160, valueFormatter: formatDateTime, cellStyle: lateCellStyle },
        { headerName: t("FP_PLAN_END_DATE"), field: "endTs", width: 160, valueFormatter: formatDateTime, cellStyle: lateCellStyle },
        { headerName: t("FP_QTY"), field: "qty", width: 100, type: ["numberColumn", "rightAligned"] }
      ]
    },
    {
      // **시간(소요) 컬럼은 전부 `TIME_COL_WIDTH` 하나로 맞춘다**(2026-08-24 사용자 지시, 2차 조정).
      // 헤더가 "작업 전 대기 시간"(8자)까지 있어서 좁으면 잘리고, 같은 계열 컬럼의 폭이 다르면
      // 그룹을 펼쳤을 때 어긋나 보인다. 값이 아니라 **헤더 길이**가 폭을 결정하는 컬럼들이다.
      headerName: t("FP_PROCESS_TM"), openByDefault: false, children: [
        { headerName: t("FP_QUEUE_TM"), field: "queueTime", width: TIME_COL_WIDTH, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" },
        { headerName: t("FP_SETUP_TM"), field: "setupTime", width: TIME_COL_WIDTH, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" },
        { headerName: t("FP_PROCESS_TM"), field: "processTime", width: TIME_COL_WIDTH, type: ["numberColumn", "rightAligned"] },
        { headerName: t("FP_WAIT_TM"), field: "waitTime", width: TIME_COL_WIDTH, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" },
        // old headerText는 `MOVE_TIME`이었으나 new 언어팩에 같은 뜻의 `FP_MOVE_TM`("작업 후 이동
        // 시간")이 이미 있어 **키를 새로 만들지 않고 그것을 쓴다**(같은 `moveTime` 필드다).
        { headerName: t("FP_MOVE_TM"), field: "moveTime", width: TIME_COL_WIDTH, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" }
      ]
    },
    {
      headerName: t("FP_PREV_JC"), openByDefault: false, children: [
        { headerName: t("JC_TIME"), field: "prevJcTime", width: TIME_COL_WIDTH, type: ["numberColumn", "rightAligned"] },
        { headerName: t("FP_JC_START_TS"), field: "prevJcStartTs", width: DATETIME_COL_WIDTH, type: ["centerAligned"], valueFormatter: formatDateTime, columnGroupShow: "open" },
        { headerName: t("FP_JC_END_TS"), field: "prevJcEndTs", width: DATETIME_COL_WIDTH, type: ["centerAligned"], valueFormatter: formatDateTime, columnGroupShow: "open" }
      ]
    },
    {
      // `FP_PREV_JC`와 폭을 공유한다(두 그룹이 나란히 펼쳐지므로 다르면 어긋나 보인다).
      // **종료 일시도 시작과 같이 넓혔다** — 시작만 넓히면 같은 그룹 안에서 짝이 어긋난다.
      headerName: t("FP_NEXT_JC"), openByDefault: false, children: [
        { headerName: t("JC_TIME"), field: "nextJcTime", width: TIME_COL_WIDTH, type: ["numberColumn", "rightAligned"] },
        { headerName: t("FP_JC_START_TS"), field: "nextJcStartTs", width: DATETIME_COL_WIDTH, type: ["centerAligned"], valueFormatter: formatDateTime, columnGroupShow: "open" },
        { headerName: t("FP_JC_END_TS"), field: "nextJcEndTs", width: DATETIME_COL_WIDTH, type: ["centerAligned"], valueFormatter: formatDateTime, columnGroupShow: "open" }
      ]
    },
    {
      headerName: t("FP_ORDER"), openByDefault: false, children: [
        { headerName: t("FP_SALES_ORDER"), field: "dmndCd", width: 200 },
        { headerName: t("FP_CUSTOMER_CD"), field: "customerCd", width: 140, columnGroupShow: "open" },
        { headerName: t("FP_CUSTOMER_NM"), field: "customerNm", width: 140, columnGroupShow: "open" },
        { headerName: t("FP_CUSTOMER_PRIORITY"), field: "customerPriority", width: 100, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" },
        { headerName: t("FP_SALES_ORDER_QTY"), field: "dmndQty", width: 110, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" },
        { headerName: t("FP_WO_CD"), field: "woCd", width: 240 },
        { headerName: t("FP_PRIORITY"), field: "woPriority", width: 90, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" },
        { headerName: t("FP_WO_ITEM_CD"), field: "woItemCd", width: 140, columnGroupShow: "open" },
        { headerName: t("FP_WO_ITEM_NM"), field: "woItemNm", width: 140, columnGroupShow: "open" },
        { headerName: t("FP_DUE_DT"), field: "dueDate", width: 160, type: ["centerAligned"], valueFormatter: formatDateTime, columnGroupShow: "open" },
        // 폭 150 — 헤더 "작업 주문 수량"이 우측 정렬 메뉴 아이콘과 겹쳐 잘렸다(2026-08-24 사용자 지시).
        { headerName: t("FP_ORDER_QTY"), field: "orderQty", width: 150, type: ["numberColumn", "rightAligned"] }
      ]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  const gridOptions = useMemo(() => {
    const base = FpGridSetting({ title: "FP_RESOURCE_PLAN_DETAILS", viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false, sortable: true, resizable: true },
      // 품목 병합(위 spanSameItem)을 켠다. `CellSpanModule`은 `common/grid.js`가 전역 등록한다.
      // **행 그룹(트리) 옵션은 없다** — 공장·자원을 문맥 밴드로 뺐으므로 평평한 그리드다.
      // 덕분에 정렬이 그룹 안에 갇히지 않고 시각·수량 기준으로 자유롭게 걸린다.
      enableCellSpan: true
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 전 행이 같은 값이라 첫 행에서 읽으면 된다(이 탭의 `rows`는 선택한 자원 1행의 활동 목록이다).
  const context = rows?.[0];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {context ? (
        <DetailContextBand
          items={[
            { labelKey: "FP_PLANT", code: context.plantCd, name: context.plantNm },
            { labelKey: "FP_RESOURCE", code: context.resourceCd, name: context.resourceNm }
          ]}
          count={rows.length}
          t={t}
        />
      ) : null}

      {/* 하단 상태바(`statusBar`)를 뺐다 — 건수는 위 밴드가 문맥과 함께 보여준다(2026-08-24 사용자 지시).
          `getRowId`도 없다: 읽기 전용이라 트랜잭션·선택 유지가 필요 없다. */}
      <Box className="ag-theme-balham grid-area" sx={{ flex: "1 1 0", minHeight: 0 }}>
        <AgGridReact
          ref={gridRef}
          rowData={rows}
          columnDefs={columnDefs}
          {...gridOptions}
        />
      </Box>
    </Box>
  );
};

export default ResourcePlanDetailTab;
